/**
 * Cycle 8 STORY-702: Suite landing page metadata.
 *
 * Hard-coded suite descriptions for Phase 1. Future cycles will move this
 * to backend `/api/catalog/suite/:name` endpoint with rich metadata.
 *
 * Each suite has: name, label, accent color, hero copy, value props,
 * use cases (industry-specific scenarios), and product list (filter by suite).
 */

export interface SuiteUseCase {
  industry: string;
  scenario: string;
  metric: string;
}

export interface SuiteMetadata {
  /** URL slug (matches /suite/:name route) */
  slug: string;
  /** Display name */
  label: string;
  /** API suite enum value (matches CatalogEntry.suite) */
  apiSuite: string;
  /** Accent color (matches API suite color) */
  accentColor: string;
  /** Hero subtitle (~ 80 chars) */
  heroTitle: string;
  /** Hero description (~ 200 chars) */
  heroSubtitle: string;
  /** 3-4 key value propositions (each ≤ 120 chars) */
  valueProps: string[];
  /** 2-3 industry-specific use cases */
  useCases: SuiteUseCase[];
  /** Long-form description (300-500 chars) */
  description: string;
}

export const suites: SuiteMetadata[] = [
  {
    slug: "safety",
    label: "Safety Suite",
    apiSuite: "safety",
    accentColor: "#2c5f2d",
    heroTitle: "Цифровизация охраны труда — от инструктажа до отчётности",
    heroSubtitle:
      "Объединённый стек продуктов для управления безопасностью на производстве. Game-механики обучения, FaceID-верификация, ML-предсказания инцидентов.",
    valueProps: [
      "Сокращение LTIFR на 30-40% за счёт игровых форматов обучения",
      "Юридически валидное доказательство прохождения инструктажа (FaceID)",
      "Автоматизация Ростехнадзор-отчётности",
      "Интеграция с действующими LMS / SCORM",
    ],
    useCases: [
      {
        industry: "горнодобыча",
        scenario: "Обязательные инструктажи перед сменой 5000+ сотрудников на 12 объектах",
        metric: "Время на инструктаж сокращено в 3 раза; вовлечённость +250%",
      },
      {
        industry: "металлургия",
        scenario: "Контроль доступа к опасным зонам с биометрической проверкой компетенции",
        metric: "Несанкционированный доступ снижен до 0 за 6 месяцев",
      },
      {
        industry: "энергетика",
        scenario: "Аудит соответствия по 800+ требованиям охраны труда + автогенерация отчётов",
        metric: "Подготовка отчётов: 40 часов → 2 часа в месяц",
      },
    ],
    description:
      "Safety Suite объединяет два флагманских продукта (shift-brief-safety + instruct-me) с экспертизой в HSE/ОТ, дополненных модулем мониторинга от core-logic. Решает задачи от ежедневных инструктажей до глубокой аналитики инцидентов. Подходит для предприятий с 500+ сотрудниками в производстве, металлургии, горнодобыче, энергетике.",
  },
  {
    slug: "logistics",
    label: "Logistics Suite",
    apiSuite: "logistics",
    accentColor: "#7a4f1a",
    heroTitle: "Оптимизация паллетирования и логистики складов",
    heroSubtitle:
      "Алгоритмы 3D-укладки + визуализация + planning-tools. От расчёта оптимальной паллеты до полного планирования отгрузки.",
    valueProps: [
      "Загрузка паллет +15-25% за счёт оптимизации алгоритмами",
      "Время планирования смены: с 4-6 часов до 30 минут",
      "Визуальная 3D-проверка укладки до отправки",
      "Интеграция с WMS (Infor, SAP, NetSuite, 1С)",
    ],
    useCases: [
      {
        industry: "retail",
        scenario: "Ежедневное планирование 200+ паллет для распределительного центра",
        metric: "Время планирования -85%; загрузка фур +18%",
      },
      {
        industry: "FMCG",
        scenario: "Оптимизация смешанных паллет (разные SKU) с учётом fragility ratings",
        metric: "Брак в транспортировке -40% за счёт правильной укладки",
      },
      {
        industry: "e-commerce",
        scenario: "Автоматический расчёт упаковки для multi-SKU заказов",
        metric: "Стоимость доставки -12%; ROI окупаемости 4 месяца",
      },
    ],
    description:
      "Logistics Suite состоит из трёх продуктов: smart-pallet (offline CLI engine) + palletix (3D web viewer) + pallet-planner (полная планировка отгрузок). От minimum-viable алгоритма укладки до enterprise-системы планирования. Подходит для retail, FMCG, e-commerce, дистрибьюторов с собственными РЦ.",
  },
  {
    slug: "sales-intelligence",
    label: "Sales Intelligence Suite",
    apiSuite: "sales",
    accentColor: "#5f2c5f",
    heroTitle: "ML-прогнозы продаж и аналитика клиентского поведения",
    heroSubtitle:
      "От пайплайна данных до dashboard для коммерсантов. Прогнозы по регионам / SKU / клиентам с учётом сезонности, промо и cross-sell.",
    valueProps: [
      "Прогнозы продаж с MAPE 12-18% (vs 25-35% классического планирования)",
      "Cross-sell / upsell-рекомендации на базе исторических корзин",
      "Учёт промо-активностей и сезонности",
      "Интеграция с CRM (Bitrix24, AmoCRM, Salesforce)",
    ],
    useCases: [
      {
        industry: "FMCG",
        scenario: "Прогноз продаж 5000 SKU × 200 точек × 12 месяцев с пересчётом еженедельно",
        metric: "Точность прогноза +45%; out-of-stock -30%",
      },
      {
        industry: "B2B-дистрибуция",
        scenario: "Upsell-рекомендации для отдела продаж по 1500 активным клиентам",
        metric: "Средний чек +22% за квартал; conversion rate +15%",
      },
      {
        industry: "retail",
        scenario: "Планирование закупок на сезон с учётом погоды и калькулятора промо",
        metric: "Излишки склада -28%; срок окупаемости 4 месяца",
      },
    ],
    description:
      "Sales Intelligence Suite — три продукта работающие в связке: sales-planner (UI + базовые прогнозы), sales-analysis (ML-движок: 6 моделей, prophet/xgboost), sales-platinum (премиум аналитика с TiDE). Подходит для коммерческих дирекций FMCG, B2B-дистрибьюторов, retail-сетей. Sell-as-Suite (D-3 решение) даёт лучший value proposition vs standalone.",
  },
];

export function getSuiteBySlug(slug: string): SuiteMetadata | undefined {
  return suites.find((s) => s.slug === slug);
}
